<template>
  <BuilderWrapper
    title="Field Calculator"
    helpful-tip="Add new fields that perform calculations on data. Define field name, formula and unit."
  >
    <b-card
      v-for="(fieldCalculation, fieldCalculationIndex) in mapFieldCalculations"
      :key="fieldCalculation.name"
      class="mb-2"
    >
      <template v-slot:header>
        <h6 class="mb-0">
          {{ fieldCalculation.name }}
        </h6>
      </template>
      <b-row>
        <b-col
          cols="12"
          class="mb-2"
        >
          {{ parseDefinitions(fieldCalculation.definitions) }} =           {{ evaluateDefinitions(fieldCalculation) }}
        </b-col>
        <b-col cols="12">
          <b-button-group class="d-flex">
            <b-button
              variant="outline-info"
              @click="editFieldCalculation(fieldCalculationIndex)"
            >
              <font-awesome-icon icon="edit" />
              Edit
            </b-button>
            <b-button
              variant="outline-danger"
              @click="removeFieldCalculation(fieldCalculation.column)"
            >
              <font-awesome-icon icon="minus" />
              Delete
            </b-button>
          </b-button-group>
        </b-col>
        <FieldDefinitionsModal
          :is-new-field="isNewField"
          :index="fieldCalculationIndex"
          :name.sync="fieldCalculation.name"
          :definitions.sync="fieldCalculation.definitions"
          :unit.sync="fieldCalculation.unit"
          :is-monetary.sync="fieldCalculation.isMonetary"
          @cancel-new-field-defs="removeFieldCalculation(fieldCalculation.column)"
        />
      </b-row>
    </b-card>
    <b-row
      class="justify-content-end"
    >
      <b-col cols="4">
        <b-button
          variant="outline-primary"
          block
          @click="addFieldCalculation"
        >
          <!-- Add -->
          <font-awesome-icon icon="plus" />
        </b-button>
      </b-col>
    </b-row>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import FieldDefinitionsModal from '@/components/builder/FieldCalculator/FieldDefinitionsModal.vue'
import { FieldCalculationV2Config, FieldCalculationDefinition, FieldTypes } from '@/types'
import {
  FIELD_CALCULATIONS_NEW_CONFIG_KEY,
  POPUP_ITEMS_CONFIG_KEY,
  SUMMARY_ITEMS_CONFIG_KEY,
} from '@/constants'

// @ts-ignore vuex-map-fields import
import { mapMultiRowFields } from 'vuex-map-fields'
// @ts-ignore uuid not having types
import { v1 as uuidv1 } from 'uuid'

const components = {
  BuilderWrapper,
  FieldDefinitionsModal,
}

@Component({
  components,
  computed: {
    ...mapMultiRowFields('primaryApp', {
      mapFieldCalculations: `appState.config.${FIELD_CALCULATIONS_NEW_CONFIG_KEY}`,
    }),
  },
})
export default class FieldCalculator extends Vue {
  mapFieldCalculations!: FieldCalculationV2Config[]

  isNewField = false

  default = {
    name: '',
    type: FieldTypes.CALCULATION,
    isMonetary: false,
    unit: '',
    definitions: [],
    column: `field-calculation-${uuidv1()}`,
  }

  get formatValueByColumn () {
    return this.$typedStore.visualisationData.formatValueByColumn
  }

  get name () {
    return this.$typedStore.visualisationApp.name
  }

  get stringifyDefinitionLabel () {
    return this.$typedStore.primary.entities.fieldCalculation.stringifyDefinitionLabel
  }

  get fieldCalculationsV2Format () {
    return this.$typedStore.primary.app.config.fieldCalculationsV2Format
  }

  set fieldCalculationsV2Format (newArray) {
    this.updateConfig(newArray, FIELD_CALCULATIONS_NEW_CONFIG_KEY)
  }

  get popupItems () {
    return this.$typedStore.primary.app.config.popupItems
  }

  set popupItems (newArray) {
    this.updateConfig(newArray, POPUP_ITEMS_CONFIG_KEY)
  }

  get summaryItems () {
    return this.$typedStore.primary.app.config.summaryItems
  }

  set summaryItems (newArray) {
    this.updateConfig(newArray, SUMMARY_ITEMS_CONFIG_KEY)
  }

  updateConfig (value: string[] | FieldCalculationV2Config[], ...args: [typeof FIELD_CALCULATIONS_NEW_CONFIG_KEY | typeof POPUP_ITEMS_CONFIG_KEY | typeof SUMMARY_ITEMS_CONFIG_KEY]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: args,
      value,
    })
  }

  async editFieldCalculation (index: number) {
    this.isNewField = false
    await this.$nextTick(() => this.$bvModal.show(`field-calculation-modal-${index}`))
  }

  async addFieldCalculation () {
    this.fieldCalculationsV2Format = [
      ...this.fieldCalculationsV2Format,
      {
        ...this.default,
        column: `field-calculation-${uuidv1()}`,
      },
    ]
    this.isNewField = true
    await this.$nextTick(() => this.$bvModal.show(`field-calculation-modal-${this.fieldCalculationsV2Format.length - 1}`))
  }

  removeFieldCalculation (column: string) {
    // You must clean up the other configs!!! Anything that uses the getter fieldMap and numericMonetaryCalculationFields because of fieldCalculations columns in it
    // TODO: The better way is to handle field that no longer exist within the store
    this.popupItems = this.popupItems.filter((item: string) => item !== column)
    this.summaryItems = this.summaryItems.filter((item: string) => item !== column)
    // Finally remove from fieldCalculations
    this.fieldCalculationsV2Format = this.fieldCalculationsV2Format.filter(({ column: col }) => col !== column)
  }

  evaluateDefinitions (fieldCalculation: FieldCalculationV2Config) {
    return this.formatValueByColumn(fieldCalculation.column)
  }

  parseDefinitions (definitions: FieldCalculationDefinition[]) {
    return definitions
      .map((def: FieldCalculationDefinition) => this.stringifyDefinitionLabel(def))
      .join(' ')
  }
}

</script>
